'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface AnnouncementBannerProps {
  message: string;
  linkUrl: string;
  linkText: string;
  emoji?: string;
  className?: string;
  dismissible?: boolean;
  dismissalKey?: string; // Use this key to store dismissal state in sessionStorage
  forceShow?: boolean; // For testing, forces the banner to show regardless of sessionStorage
}

export function AnnouncementBanner({
  message,
  linkUrl,
  linkText,
  emoji = '🚀',
  className,
  dismissible = true,
  dismissalKey = 'announcement-dismissed',
  forceShow = false,
}: AnnouncementBannerProps) {
  const [isVisible, setIsVisible] = useState(true); // Default to visible

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
  };

  return (
    <div 
      className={cn(
        'w-full bg-gradient-to-r from-purple-600 via-indigo-500 to-blue-500 text-white py-2 sm:py-3 px-4 shadow-md relative animate-appear z-[51] transition-all duration-300 ease-in-out',
        'before:absolute before:inset-0 before:bg-[url("/noise.svg")] before:opacity-5 before:z-[-1] overflow-hidden',
        className
      )}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-12 -top-12 h-24 w-24 rounded-full bg-white/10 blur-xl"></div>
        <div className="absolute -right-12 -bottom-12 h-24 w-24 rounded-full bg-white/10 blur-xl"></div>
      </div>
      
      <div className="container mx-auto flex items-center justify-center">
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-sm sm:text-base md:gap-x-3 relative">
          <span className="flex items-center font-medium">
            <span className="mr-2 text-xl">{emoji}</span>
            {message}
          </span>
          
          <Link 
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex items-center justify-center gap-1 bg-white/20 hover:bg-white/30 rounded-full px-3 sm:px-4 py-1 font-medium text-white transition-colors"
          >
            {linkText}
            <ArrowRight className="h-3 w-3" />
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white animate-pulse"></span>
          </Link>
        </div>
        
        {dismissible && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Dismiss announcement"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
} 